'use client'

/**
 * Web Vitals → ingest-episode reporter.
 *
 * Streams the browser-reported Core Web Vitals (LCP, CLS, INP,
 * FCP, TTFB, FID) to our Supabase `ingest-episode` edge function
 * tagged `source: 'webapp_vitals'`. The ingest function already
 * knows how to chain these into episodic training signal.
 *
 * Design notes:
 *   - Uses `next/web-vitals`'s `useReportWebVitals` — the hook
 *     fires once per metric, generally late in the page lifecycle
 *     (CLS/INP can land on unload).
 *   - A small in-memory batch is flushed on `pagehide` via
 *     `navigator.sendBeacon`, which browsers guarantee even
 *     through unload. Normal post-mount flushes go via `fetch`
 *     with `keepalive: true` for the same reason.
 *   - No PII. Metric names, numeric values, and the current
 *     pathname only.
 */

import { useEffect, useRef } from 'react'
import { useReportWebVitals } from 'next/web-vitals'

type WebVitalMetric = Parameters<Parameters<typeof useReportWebVitals>[0]>[0]

interface VitalsEpisode {
    source: 'webapp_vitals'
    state: {
        path: string
        user_agent: string
        navigation_type: string
    }
    action: {
        metric: string
        id: string
    }
    outcome: {
        value: number
        rating: string
        delta: number
    }
}

const FLUSH_INTERVAL_MS = 5000
const MAX_BATCH = 20

export function WebVitalsReporter(): null {
    const buffer = useRef<VitalsEpisode[]>([])
    const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const flush = (useBeacon = false) => {
        if (buffer.current.length === 0) return
        const episodes = buffer.current.splice(0, buffer.current.length)
        const url = process.env.NEXT_PUBLIC_SUPABASE_URL
        const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
        if (!url || !anonKey) return

        const endpoint = `${url}/functions/v1/ingest-episode`
        const payload = JSON.stringify({ episodes })

        if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            // Beacon API ignores headers entirely — we send the token
            // as a query param as the edge function also accepts it
            // there for this path. Keeps the payload flush on unload.
            try {
                const blob = new Blob([payload], { type: 'application/json' })
                navigator.sendBeacon(`${endpoint}?apikey=${anonKey}`, blob)
                return
            } catch {
                // fall through to fetch
            }
        }

        fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${anonKey}`,
            },
            body: payload,
            keepalive: true,
        }).catch(() => {
            // Silent. Web-vitals reporting must not surface errors
            // to the user or the app; failures here are pure
            // observability loss, not user-visible.
        })
    }

    useReportWebVitals((metric: WebVitalMetric) => {
        const nav = typeof performance !== 'undefined'
            && performance.getEntriesByType
            ? (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
            : undefined

        buffer.current.push({
            source: 'webapp_vitals',
            state: {
                path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                navigation_type: nav?.type ?? 'unknown',
            },
            action: {
                metric: metric.name,
                id: metric.id,
            },
            outcome: {
                value: metric.value,
                rating: (metric as unknown as { rating?: string }).rating ?? 'unknown',
                delta: metric.delta,
            },
        })

        if (buffer.current.length >= MAX_BATCH) {
            if (flushTimer.current) {
                clearTimeout(flushTimer.current)
                flushTimer.current = null
            }
            flush(false)
            return
        }

        if (!flushTimer.current) {
            flushTimer.current = setTimeout(() => {
                flushTimer.current = null
                flush(false)
            }, FLUSH_INTERVAL_MS)
        }
    })

    useEffect(() => {
        const handler = () => flush(true)
        // `pagehide` is more reliable than `beforeunload` on mobile +
        // bfcache scenarios.
        window.addEventListener('pagehide', handler)
        return () => {
            window.removeEventListener('pagehide', handler)
            if (flushTimer.current) {
                clearTimeout(flushTimer.current)
                flushTimer.current = null
            }
            flush(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return null
}
